
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Sorting} from './Sorting';
import {FieldModelSorting} from './Sorting';

/**
 * AccountGroupSorting struct
 */
class AccountGroupSorting {
  /**
   * Initialize struct
   * @param {Sorting=} name
   * @constructor
   */
  constructor (name = null) {
    this.Name = name
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountGroupSorting}
   * @param {!AccountGroupSorting} other Other struct
   * @returns {!AccountGroupSorting} This struct
   */
  copy (other) {
    if (other.Name != null) {
      this.Name = Sorting.fromObject(other.Name)
    } else {
      this.Name = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountGroupSorting}
   * @returns {!AccountGroupSorting} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountGroupSortingModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountGroupSortingModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountGroupSorting}
   * @param {!AccountGroupSorting} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountGroupSorting)) {
      throw new TypeError('Instance of AccountGroupSorting is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountGroupSorting}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Name: ((this.Name != null) ? this.Name : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountGroupSorting.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountGroupSorting} other Object value
   * @returns {!AccountGroupSorting} Created struct
   */
  static fromObject (other) {
    return new AccountGroupSorting().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountGroupSorting}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountGroupSorting.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountGroupSorting}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 152
  }
}

export { AccountGroupSorting };

/**
 * Fast Binary Encoding AccountGroupSorting field model
 */
class FieldModelAccountGroupSorting extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Name = new fbe.FieldModelOptional(new FieldModelSorting(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Name.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Name.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountGroupSorting.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 152
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountGroupSorting}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountGroupSorting}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountGroupSorting}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountGroupSorting}
   * @param {!AccountGroupSorting} fbeValue Default value, defaults is new AccountGroupSorting()
   * @returns {!AccountGroupSorting} AccountGroupSorting value
   */
  get (fbeValue = new AccountGroupSorting()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountGroupSorting}
   * @param {!AccountGroupSorting} fbeValue AccountGroupSorting value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountGroupSorting}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountGroupSorting}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountGroupSorting}
   * @param {!AccountGroupSorting} fbeValue AccountGroupSorting value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountGroupSorting}
   * @param {AccountGroupSorting} fbeValue AccountGroupSorting value
   */
  setFields (fbeValue) {
    this.Name.set(fbeValue.Name)
  }
}

export { FieldModelAccountGroupSorting };

/**
 * Fast Binary Encoding AccountGroupSorting model
 */
class AccountGroupSortingModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountGroupSorting(this.buffer, 4)
  }

  /**
   * Get the AccountGroupSorting model
   * @this {!AccountGroupSortingModel}
   * @returns {!FieldModelAccountGroupSorting} model AccountGroupSorting model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountGroupSortingModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountGroupSortingModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountGroupSortingModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountGroupSortingModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountGroupSorting.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountGroupSortingModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountGroupSortingModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountGroupSortingModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountGroupSortingModel}
   * @param {!AccountGroupSorting} value AccountGroupSorting value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountGroupSortingModel}
   * @param {!AccountGroupSorting} value AccountGroupSorting value, defaults is new AccountGroupSorting()
   * @return {!object} Deserialized AccountGroupSorting value and its size
   */
  deserialize (value = new AccountGroupSorting()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountGroupSorting(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountGroupSorting(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountGroupSortingModel}
   * @param {!number} prev Previous AccountGroupSorting model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountGroupSortingModel };
